import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calculator, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

function CompoundInterest() {
  // Form state
  const [startBalance, setStartBalance] = useState(1000);
  const [leverage, setLeverage] = useState(10);
  const [days, setDays] = useState(30);
  const [dailyPercent, setDailyPercent] = useState(5);
  const [successRate, setSuccessRate] = useState(80); // Процент успешных сделок

  // Results state
  const [results, setResults] = useState(null);

  const calculateCompoundInterest = () => {
    if (startBalance <= 0 || leverage <= 0 || days <= 0 || dailyPercent <= 0 || successRate < 0 || successRate > 100) {
      toast.error('Проверьте введенные данные');
      return;
    }

    const dailyResults = [];
    let currentBalance = startBalance;
    let totalProfit = 0;

    for (let day = 1; day <= days; day++) {
      // Определяем, успешна ли сделка в этот день (на основе процента успеха)
      const isSuccessful = Math.random() * 100 < successRate;

      let dayProfit = 0;
      if (isSuccessful) {
        // Прибыль = текущий_баланс * (процент_изменения / 100) * плечо
        dayProfit = currentBalance * (dailyPercent / 100) * leverage;
        currentBalance += dayProfit;
        totalProfit += dayProfit;
      }

      dailyResults.push({
        day,
        balance: Math.round(currentBalance * 100) / 100,
        profit: Math.round(dayProfit * 100) / 100,
        successful: isSuccessful
      });
    }

    const roi = ((currentBalance - startBalance) / startBalance) * 100;

    setResults({
      dailyResults,
      finalBalance: Math.round(currentBalance * 100) / 100,
      totalProfit: Math.round(totalProfit * 100) / 100,
      roi: Math.round(roi * 100) / 100
    });

    toast.success('Расчет выполнен');
  };

  const chartData = results?.dailyResults.map(r => ({
    day: r.day,
    balance: r.balance
  })) || [];

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Калькулятор сложного процента
          </CardTitle>
          <CardDescription className="text-slate-400">
            Рассчитайте рост баланса с реинвестированием и учетом плеча
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startBalance" className="text-slate-300">Стартовый баланс (USD)</Label>
              <Input
                id="startBalance"
                type="number"
                value={startBalance}
                onChange={(e) => setStartBalance(e.target.value === '' ? '' : parseFloat(e.target.value))}
                onBlur={(e) => setStartBalance(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="bg-slate-900/80 border-slate-600 text-white mt-2"
                placeholder="1000"
              />
            </div>
            <div>
              <Label htmlFor="leverage" className="text-slate-300">Плечо (L)</Label>
              <Input
                id="leverage"
                type="number"
                value={leverage}
                onChange={(e) => setLeverage(e.target.value === '' ? '' : parseFloat(e.target.value))}
                onBlur={(e) => setLeverage(e.target.value === '' ? 1 : parseFloat(e.target.value))}
                className="bg-slate-900/80 border-slate-600 text-white mt-2"
                placeholder="10"
              />
            </div>
            <div>
              <Label htmlFor="days" className="text-slate-300">Количество дней</Label>
              <Input
                id="days"
                type="number"
                value={days}
                onChange={(e) => setDays(e.target.value === '' ? '' : parseInt(e.target.value))}
                onBlur={(e) => setDays(e.target.value === '' ? 1 : parseInt(e.target.value))}
                className="bg-slate-900/80 border-slate-600 text-white mt-2"
                placeholder="30"
              />
            </div>
            <div>
              <Label htmlFor="dailyPercent" className="text-slate-300">Дневной % изменения цены</Label>
              <Input
                id="dailyPercent"
                type="number"
                step="0.1"
                value={dailyPercent}
                onChange={(e) => setDailyPercent(e.target.value === '' ? '' : parseFloat(e.target.value))}
                onBlur={(e) => setDailyPercent(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="bg-slate-900/80 border-slate-600 text-white mt-2"
                placeholder="5"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="successRate" className="text-slate-300">Процент успешных сделок (%)</Label>
              <Input
                id="successRate"
                type="number"
                min="0"
                max="100"
                value={successRate}
                onChange={(e) => setSuccessRate(e.target.value === '' ? '' : parseFloat(e.target.value))}
                onBlur={(e) => setSuccessRate(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="bg-slate-900/80 border-slate-600 text-white mt-2"
                placeholder="80"
              />
              <p className="text-xs text-slate-500 mt-1">Не каждый день удается словить нужный процент (0-100%)</p>
            </div>
          </div>

          <Button 
            onClick={calculateCompoundInterest}
            className="w-full bg-gradient-to-r bg-blue-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-4 md:py-6 shadow-lg text-base md:text-lg"
          >
            <Calculator className="w-5 h-5 mr-2" />
            Рассчитать
          </Button>
        </CardContent>
      </Card>

      {results && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
              <CardHeader className="pb-3">
                <CardDescription className="text-slate-400">Начальный баланс</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">${startBalance.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
              <CardHeader className="pb-3">
                <CardDescription className="text-slate-400">Итоговый баланс</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">${results.finalBalance.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border-slate-700">
              <CardHeader className="pb-3">
                <CardDescription className="text-slate-400">ROI</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-400">
                  +{results.roi}%
                </div>
                <div className="text-sm text-blue-300 mt-1">
                  +${results.totalProfit.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Balance Chart */}
          <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-white">График роста баланса</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250} className="md:h-[300px]">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#94a3b8" 
                    label={{ value: 'День', position: 'insideBottom', offset: -5, fill: '#94a3b8' }} 
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    label={{ value: 'Баланс (USD)', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} 
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                    labelStyle={{ color: '#f1f5f9' }}
                    itemStyle={{ color: '#10b981' }}
                  />
                  <Legend wrapperStyle={{ color: '#94a3b8' }} />
                  <Line 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#10b981" 
                    strokeWidth={3} 
                    name="Баланс" 
                    dot={{ fill: '#10b981', r: 4 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-white">Детальные результаты</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-96 overflow-y-auto -mx-6 px-6 md:mx-0 md:px-0">
                <table className="w-full text-xs md:text-sm min-w-[600px]">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">День</th>
                      <th className="text-right py-3 px-4 text-slate-300 font-semibold">Баланс</th>
                      <th className="text-right py-3 px-4 text-slate-300 font-semibold">Прибыль за день</th>
                      <th className="text-center py-3 px-4 text-slate-300 font-semibold">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.dailyResults.map((result) => (
                      <tr key={result.day} className="border-b border-slate-700/50 hover:bg-slate-900/30">
                        <td className="py-3 px-4 text-slate-300">{result.day}</td>
                        <td className="py-3 px-4 text-right text-white font-semibold">
                          ${result.balance.toLocaleString()}
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold ${
                          result.profit > 0 ? 'text-blue-400' : 'text-slate-500'
                        }`}>
                          {result.profit > 0 ? '+' : ''}${result.profit.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {result.successful ? (
                            <span className="inline-flex items-center gap-1 text-blue-400">
                              <TrendingUp className="w-4 h-4" />
                              Успех
                            </span>
                          ) : (
                            <span className="text-slate-500">Пропуск</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default CompoundInterest;
